import { User } from '../models/User';
import { RoutePermissions } from '../constants/RoutePermissions';
import { ActiveProduct } from '../models/ActiveProduct';
import { ExtendedProducts } from '../models/ExtendedProducts';
import { BrandingHiddenSections } from '../main/constants/BrandingHiddenSections';
import { DefaultRoutePermissions } from '../constants/DefaultRoutePermissions';

export interface IPermissionsProps {
    user?: User;
    extendedProducts?: ExtendedProducts;
    activeProducts?: ActiveProduct[];
    permissions?: Array<RoutePermissions[keyof RoutePermissions]>;
    routeFeatures?: Array<RoutePermissions[keyof RoutePermissions]>;
}

export const getPermissions = ({ user, extendedProducts, activeProducts, permissions }: IPermissionsProps) => {
    const hasNoPermissions = permissions === null || permissions === undefined;

    if (user && hasNoPermissions) {
        return getLicenceFeatures({ user, extendedProducts, activeProducts });
    }
    return permissions;
};

export const getLicenceFeatures = ({
    user,
    extendedProducts,
    activeProducts,
}: IPermissionsProps): Array<RoutePermissions[keyof RoutePermissions]> => {
    const { codename } = (user as User) || {};
    const { newTemplates, newMicroSettings } = extendedProducts?.account ?? {};

    const newTemplatesPermission = newTemplates
        ? [RoutePermissions.ShowNewTemplates]
        : [RoutePermissions.ShowLegacyTemplates];

    const newSettingsPermission = newMicroSettings
        ? [RoutePermissions.ShowNewSettings]
        : [RoutePermissions.ShowLegacySettings];

    const licensePermissions: { [license: string]: () => Array<RoutePermissions[keyof RoutePermissions]> } = {
        demo: () =>
            getAccountFeatures({
                user,
                extendedProducts,
                activeProducts,
                routeFeatures: [
                    ...newTemplatesPermission,
                    ...newSettingsPermission
                ],
            }),
        pro: () =>
            getAccountFeatures({
                user,
                extendedProducts,
                activeProducts,
                routeFeatures: [
                    ...newTemplatesPermission,
                    ...newSettingsPermission
                ],
            }),
        personal: () =>
            getAccountFeatures({
                user,
                extendedProducts,
                activeProducts,
                routeFeatures: [
                    ...newTemplatesPermission,
                    ...newSettingsPermission
                ],
            }),
        business: () =>
            getAccountFeatures({
                user,
                extendedProducts,
                activeProducts,
                routeFeatures: [
                    RoutePermissions.ShowLegacyTemplates,
                    ...newSettingsPermission
                ],
            }),
        developer: () =>
            getAccountFeatures({
                user,
                extendedProducts,
                activeProducts,
                routeFeatures: [
                    ...newTemplatesPermission,
                    ...newSettingsPermission
                ],
            }),
        business2020: () =>
            getAccountFeatures({
                user,
                extendedProducts,
                activeProducts,
                routeFeatures: [
                    ...newTemplatesPermission,
                    ...newSettingsPermission
                ],
            }),
        'business-plus': () =>
            getAccountFeatures({
                user,
                extendedProducts,
                activeProducts,
                routeFeatures: [
                    RoutePermissions.ShowLegacyTemplates,
                    ...newSettingsPermission
                ],
            }),
        'business-plus-2020': () =>
            getAccountFeatures({
                user,
                extendedProducts,
                activeProducts,
                routeFeatures: [
                    ...newTemplatesPermission,
                    ...newSettingsPermission
                ],
            }),
        enterprise: () =>
            getAccountFeatures({
                user,
                extendedProducts,
                activeProducts,
                routeFeatures: [
                    ...newTemplatesPermission,
                    ...newSettingsPermission
                ],
            }),
        reseller: () => [RoutePermissions.Reseller],
    };

    return licensePermissions[codename]();
};

export const getAccountFeatures = ({
    user,
    extendedProducts,
    activeProducts,
    routeFeatures,
}: IPermissionsProps): Array<RoutePermissions[keyof RoutePermissions]> => {
    let permissions: Array<RoutePermissions[keyof RoutePermissions]> = Object.values(DefaultRoutePermissions);

    const { hasApi, isPayingUser, isAdmin, isTrialAccount } = (user as User) || {};

    const hasActiveProducts = activeProducts && activeProducts.length > 0;
    const hasCertificateSection = extendedProducts?.account.certificateSection;
    const isPayingAndAdminUser = isPayingUser && isAdmin;

    if (routeFeatures) {
        permissions = permissions.concat(routeFeatures);
    }

    if (isTrialAccount) {
        permissions.push(RoutePermissions.ShowHomeSection);
    }

    if (hasCertificateSection) {
        permissions.push(RoutePermissions.CertificateSection);
    }

    if (isPayingAndAdminUser) {
        permissions.push(RoutePermissions.SubscriptionSection);
    }

    if (hasApi) {
        permissions.push(RoutePermissions.HasApi);
    }

    if (isAdmin) {
        permissions.push(RoutePermissions.IsAdmin);
    }

    if (hasActiveProducts) {
        permissions.push(RoutePermissions.ShowGauges);
    }
    return permissions;
};

export const hasPermission = (permissionsProps: IPermissionsProps, permissionType: string) =>
    (permissionsProps && getPermissions(permissionsProps)?.includes(permissionType)) || false;

export const isVisibleByBranding = (
    hiddenSections: BrandingHiddenSections[] | undefined,
    section: BrandingHiddenSections
) => !hiddenSections?.includes(section);
